import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Camera, CameraOff, Car, Bike, LogIn, CalendarDays,
  Loader2, ScanLine, Settings, ChevronDown
} from 'lucide-react';
import BackButton from '../components/BackButton';
import AppFooter from '../components/AppFooter';
import ReceiptImageInput from '../components/ReceiptImageInput';
import {
  CAMERA_OPTIONS,
  DEFAULT_CAMERA,
  requestCameraStream,
  stopCameraStream,
} from '../lib/camera';
import { getParkingPrice } from '../lib/parkingPrices';
import { OCR_DETECTION_TIMEOUT_MS } from '../lib/ocrAutoGate';

function formatOcrError(err) {
  if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
    return 'OCR demoró demasiado. Ingresá la patente manualmente.';
  }
  if (err?.response?.data?.detail) return err.response.data.detail;
  return 'Error en OCR. Ingresá la patente manualmente.';
}

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const CASM_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dmndty59_San_Martin_deTucuman.png";

export default function DetectionPage() {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [autoMode, setAutoMode] = useState(false);
  const [plateInput, setPlateInput] = useState('');
  const [vehicleType, setVehicleType] = useState('auto');
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [ocrResults, setOcrResults] = useState([]);
  const [isOcrRunning, setIsOcrRunning] = useState(false);
  const streamRef = useRef(null);
  const autoIntervalRef = useRef(null);
  const lastAutoPlateRef = useRef(null);
  const ocrManualAbortRef = useRef(null);
  const ocrAutoAbortRef = useRef(null);
  const [receiptImage, setReceiptImage] = useState(null);
  const [selectedCamera, setSelectedCamera] = useState(DEFAULT_CAMERA);
  const [showSettings, setShowSettings] = useState(false);
  const [ocrSettings, setOcrSettings] = useState({
    sensitivity: 15,
    quality: 70,
    resolution: 640,
    interval: 1500,
  });
  const [ocrEngineReady, setOcrEngineReady] = useState(null);
  // v1.2.1 — Optional event addon for OCR/manual entries on this page
  const [tariff, setTariff] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [addonEnabled, setAddonEnabled] = useState(false);
  const [addonEventId, setAddonEventId] = useState('');

  // Load tariff, warm up OCR models, and fetch event types for addon entries.
  useEffect(() => {
    (async () => {
      try {
        axios.post(`${API}/detect/ocr-warmup`, null, { withCredentials: true }).catch(() => {});
        const [tRes, evRes, ocrRes] = await Promise.all([
          axios.get(`${API}/tariff`, { withCredentials: true }),
          axios.get(`${API}/event-types?only_active=true`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/detect/ocr-status`, { withCredentials: true }).catch(() => ({ data: {} })),
        ]);
        setTariff(tRes.data);
        setEventTypes(evRes.data || []);
        setOcrEngineReady(Boolean(ocrRes.data?.ready));
        if (tRes.data?.event_addon_mode === 'required') {
          setAddonEnabled(true);
          if (tRes.data?.event_addon_default_id) setAddonEventId(tRes.data.event_addon_default_id);
        } else if (tRes.data?.event_addon_mode === 'optional' && tRes.data?.event_addon_default_id) {
          setAddonEventId(tRes.data.event_addon_default_id);
        }
      } catch {}
    })();
  }, []);

  const addonMode = tariff?.event_addon_mode || 'disabled';
  const addonAvailable = addonMode !== 'disabled' && eventTypes.length > 0;
  const addonRequired = addonMode === 'required';
  const addonLabel = tariff?.event_addon_label || 'Activar evento';
  const formatCurrency = (a) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(a || 0);
  const buildEntryPayload = useCallback((basePayload) => {
    const payload = { ...basePayload, vehicle_type: vehicleType, service_type: 'parking' };
    if ((addonEnabled || addonRequired) && addonEventId) {
      payload.addon_event_type_id = addonEventId;
    }
    if (receiptImage) {
      payload.receipt_image = receiptImage;
    }
    return payload;
  }, [addonEnabled, addonRequired, addonEventId, vehicleType, receiptImage]);

  const startCamera = async (facingMode = selectedCamera) => {
    try {
      stopCameraStream(streamRef.current);
      const stream = await requestCameraStream(facingMode);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsStreaming(true);
      }
    } catch (err) {
      toast.error(err.message || 'No se pudo acceder a la cámara');
    }
  };

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      stopCameraStream(streamRef.current);
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    if (autoIntervalRef.current) {
      clearInterval(autoIntervalRef.current);
      autoIntervalRef.current = null;
    }
    setIsStreaming(false);
    setAutoMode(false);
  }, []);

  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current;
    if (video.readyState < 2) return null;
    const canvas = canvasRef.current;
    const w = ocrSettings.resolution;
    canvas.width = w;
    canvas.height = Math.round(w * video.videoHeight / video.videoWidth) || 270;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', ocrSettings.quality / 100);
  }, [ocrSettings]);

  const postOcrRequest = useCallback(async (imageData, abortRef) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    return axios.post(
      `${API}/detect/ocr`,
      { image: imageData, min_confidence: ocrSettings.sensitivity / 100 },
      {
        withCredentials: true,
        timeout: OCR_DETECTION_TIMEOUT_MS,
        signal: controller.signal,
      },
    );
  }, [ocrSettings.sensitivity]);

  // Manual OCR — fills input; manual entry remains available while processing.
  const runManualOCR = useCallback(async () => {
    const imageData = captureFrame();
    if (!imageData) return;
    if (ocrEngineReady === false) {
      axios.post(`${API}/detect/ocr-warmup`, null, { withCredentials: true }).catch(() => {});
    }
    setIsOcrRunning(true);
    try {
      const { data } = await postOcrRequest(imageData, ocrManualAbortRef);
      setOcrResults(data.plates || []);
      if (data.plates && data.plates.length > 0) {
        setPlateInput(data.plates[0].plate);
        toast.success(`Patente detectada: ${data.plates[0].plate}`);
      } else {
        toast.error('No se detectaron patentes. Ingresá la patente manualmente.');
      }
    } catch (err) {
      if (err?.code === 'ERR_CANCELED') return;
      toast.error(formatOcrError(err));
    } finally {
      setIsOcrRunning(false);
    }
  }, [captureFrame, postOcrRequest, ocrEngineReady]);

  // Auto mode: OCR + auto entry only (no automatic exit).
  const runAutoOCR = useCallback(async () => {
    if (isOcrRunning) return;
    const imageData = captureFrame();
    if (!imageData) return;

    setIsOcrRunning(true);
    try {
      const { data } = await postOcrRequest(imageData, ocrAutoAbortRef);

      if (data.plates && data.plates.length > 0) {
        const bestPlate = data.plates[0].plate;
        setOcrResults(data.plates);
        setPlateInput(bestPlate);

        if (bestPlate !== lastAutoPlateRef.current) {
          lastAutoPlateRef.current = bestPlate;
          try {
            if (addonRequired && !addonEventId) {
              toast.error('Configurá el evento adicional antes de cobrar entradas');
            } else {
              const payload = buildEntryPayload({ plate: bestPlate, entry_type: 'auto-ocr' });
              const { data: entryData } = await axios.post(
                `${API}/vehicles/entry`,
                payload,
                { withCredentials: true },
              );
              setLastAction({
                type: 'entry', plate: bestPlate, is_member: entryData.is_member,
                event_addon_name: entryData.event_addon_name,
                event_amount: entryData.event_amount,
                parking_amount: entryData.parking_amount,
                total_amount: entryData.total_amount,
              });
              const evLbl = entryData.event_addon_name ? ` [+ ${entryData.event_addon_name}]` : '';
              toast.success(`ENTRADA: ${bestPlate}${entryData.is_member ? ' (SOCIO)' : ''}${evLbl}`);
              setReceiptImage(null);
            }
            setPlateInput('');
            setTimeout(() => { lastAutoPlateRef.current = null; setLastAction(null); }, 10000);
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al registrar');
            setTimeout(() => { lastAutoPlateRef.current = null; }, 5000);
          }
        }
      } else {
        setOcrResults([]);
      }
    } catch (err) {
      if (err?.code === 'ERR_CANCELED') return;
      // Auto OCR retries on next tick; avoid toast spam while the camera is looping.
    } finally {
      setIsOcrRunning(false);
    }
  }, [isOcrRunning, captureFrame, postOcrRequest, addonRequired, addonEventId, buildEntryPayload]);

  // Auto mode polling
  useEffect(() => {
    if (autoMode && isStreaming) {
      autoIntervalRef.current = setInterval(runAutoOCR, ocrSettings.interval);
    } else {
      if (autoIntervalRef.current) {
        clearInterval(autoIntervalRef.current);
        autoIntervalRef.current = null;
      }
      lastAutoPlateRef.current = null;
    }
    return () => {
      if (autoIntervalRef.current) clearInterval(autoIntervalRef.current);
    };
  }, [autoMode, isStreaming, runAutoOCR, ocrSettings.interval]);

  useEffect(() => {
    return () => {
      // The latest controllers, rather than those present when the effect ran,
      // must be cancelled when the page unmounts.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ocrManualAbortRef.current?.abort();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ocrAutoAbortRef.current?.abort();
      stopCamera();
    };
  }, [stopCamera]);

  const handleEntry = async () => {
    if (!plateInput.trim()) { toast.error('Ingresa una patente'); return; }
    if ((addonEnabled || addonRequired) && !addonEventId) {
      toast.error('Selecciona el tipo de evento');
      return;
    }
    setIsProcessing(true);
    try {
      const payload = buildEntryPayload({ plate: plateInput.toUpperCase(), entry_type: 'camera' });
      const { data } = await axios.post(`${API}/vehicles/entry`, payload, { withCredentials: true });
      setLastAction({
        type: 'entry', plate: data.plate, is_member: data.is_member,
        event_addon_name: data.event_addon_name, event_amount: data.event_amount,
      });
      const evLbl = data.event_addon_name ? ` + ${data.event_addon_name}` : '';
      toast.success(`Entrada: ${data.plate}${data.is_member ? ' (SOCIO)' : ''}${evLbl}`);
      setPlateInput('');
      setReceiptImage(null);
      if (!addonRequired) { setAddonEnabled(false); setAddonEventId(''); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-16 items-center justify-between gap-3 py-2">
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              <BackButton to="/dashboard" />
              <div className="min-w-0 border-l border-slate-700 pl-2 sm:pl-4">
                <h1 className="text-base font-bold leading-tight text-white sm:text-lg">Detección de Patentes</h1>
                <p className="text-xs text-slate-400 sm:text-sm">
                  {autoMode ? 'Modo Automatico — OCR + Entrada' : 'Modo Manual'}
                </p>
              </div>
            </div>
            <img src={LOGO_URL} alt="ParkingVision AI" className="hidden h-8 w-auto shrink-0 sm:block" />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
          <div className="lg:col-span-2">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="p-3 sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-white flex items-center gap-2 flex-wrap">
                    <Camera className="w-5 h-5" />Vista de Camara
                    {ocrEngineReady === false && (
                      <Badge className="bg-amber-700 text-xs">OCR cargando…</Badge>
                    )}
                  </CardTitle>
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                    <select
                      value={selectedCamera}
                      onChange={(e) => {
                        const facingMode = e.target.value;
                        setSelectedCamera(facingMode);
                        if (isStreaming) startCamera(facingMode);
                      }}
                      className="w-full min-w-0 rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-sm text-slate-200 sm:w-auto"
                      data-testid="camera-select"
                      aria-label="Seleccionar cámara"
                    >
                      {CAMERA_OPTIONS.map((camera) => (
                        <option key={camera.value} value={camera.value}>{camera.label}</option>
                      ))}
                    </select>
                    {isStreaming && (
                      <div className="flex items-center gap-2 bg-slate-700 rounded-lg px-3 py-1.5">
                        <span className="text-slate-300 text-sm font-medium">
                          {autoMode ? 'AUTO' : 'MANUAL'}
                        </span>
                        <Switch
                          checked={autoMode}
                          onCheckedChange={(v) => {
                            if (v && addonRequired && !addonEventId) {
                              toast.error('Selecciona el evento adicional antes de activar Auto');
                              return;
                            }
                            setAutoMode(v);
                          }}
                          data-testid="auto-mode-switch"
                        />
                      </div>
                    )}
                    {!isStreaming ? (
                      <Button onClick={() => startCamera()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="start-camera-btn">
                        <Camera className="w-4 h-4 mr-2" />Iniciar Camara
                      </Button>
                    ) : (
                      <Button onClick={stopCamera} variant="destructive" data-testid="stop-camera-btn">
                        <CameraOff className="w-4 h-4 mr-2" />Detener
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="relative aspect-video w-full max-w-full overflow-hidden rounded bg-slate-900">
                  <video ref={videoRef} autoPlay playsInline muted
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: isStreaming ? 'block' : 'none' }}
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  {!isStreaming && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-slate-400">
                        <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>Haz clic en "Iniciar Camara"</p>
                      </div>
                    </div>
                  )}
                  {isOcrRunning && (
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-amber-600 text-base px-3 py-1">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />OCR...
                      </Badge>
                    </div>
                  )}
                  {ocrResults.length > 0 && (
                    <div className="absolute bottom-4 left-4">
                      <Badge className="bg-amber-600 text-xl px-4 py-2 font-mono">
                        <ScanLine className="w-5 h-5 mr-2" />{ocrResults[0].plate} ({Math.round(ocrResults[0].confidence * 100)}%)
                      </Badge>
                    </div>
                  )}
                  {autoMode && (
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-emerald-600 text-sm px-3 py-1">
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        Modo Automatico activo
                      </Badge>
                    </div>
                  )}
                </div>
                
                {/* Manual mode buttons — only show when NOT in auto mode */}
                {isStreaming && !autoMode && (
                  <div className="mt-4">
                    <Button onClick={runManualOCR} disabled={isOcrRunning} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="ocr-btn">
                      {isOcrRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ScanLine className="w-4 h-4 mr-2" />}
                      Leer Patente (OCR)
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            {/* Last action */}
            {lastAction && (
              <Card className="bg-emerald-900/50 border-emerald-600">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <LogIn className="w-5 h-5 text-emerald-400" />
                    <span className="font-bold text-emerald-400">ENTRADA</span>
                    {lastAction.is_member && <Badge className="bg-blue-600 text-xs">SOCIO</Badge>}
                  </div>
                  <p className="text-2xl font-mono font-bold text-white">{lastAction.plate}</p>
                </CardContent>
              </Card>
            )}

            {/* OCR Results */}
            {ocrResults.length > 0 && !autoMode && (
              <Card className="bg-amber-900/30 border-amber-700/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-amber-300 text-base flex items-center gap-2">
                    <ScanLine className="w-4 h-4" />Patentes Detectadas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {ocrResults.map((plate, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-slate-800 rounded cursor-pointer hover:bg-slate-700"
                        onClick={() => setPlateInput(plate.plate)} data-testid={`ocr-result-${i}`}>
                        <span className="font-mono font-bold text-white text-lg">{plate.plate}</span>
                        <Badge className="bg-amber-600">{Math.round(plate.confidence * 100)}%</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Manual entry — always available (OCR optional). */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-base">Registro de Entrada</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm mb-2 block">Patente</label>
                  <Input
                    value={plateInput}
                    onChange={(e) => setPlateInput(e.target.value.toUpperCase())}
                    placeholder="ABC123"
                    className="bg-slate-700 border-slate-600 text-white font-mono text-lg text-center uppercase"
                    data-testid="plate-input"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm text-slate-400">Tipo de vehículo</label>
                  <Select value={vehicleType} onValueChange={setVehicleType}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="detection-vehicle-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="auto"><span className="flex items-center gap-2"><Car className="h-4 w-4" />Auto — {formatCurrency(getParkingPrice('auto', tariff))}</span></SelectItem>
                      <SelectItem value="moto"><span className="flex items-center gap-2"><Bike className="h-4 w-4" />Moto — {formatCurrency(getParkingPrice('moto', tariff))}</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-2 block text-sm text-slate-400">Comprobante de pago (opcional)</label>
                  <ReceiptImageInput value={receiptImage} onChange={setReceiptImage} disabled={isProcessing} />
                </div>
                <div>
                  <Button onClick={handleEntry} disabled={isProcessing} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="entry-btn">
                    <LogIn className="w-4 h-4 mr-1" />Entrada
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* v1.2.1 — Optional event addon for OCR/manual entries (admin-controlled) */}
            {addonAvailable && (
              <Card className="bg-amber-900/10 border-amber-500/40" data-testid="ocr-event-addon">
                <CardHeader className="pb-2">
                  <CardTitle className="text-amber-200 text-base flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />Evento adicional
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <button
                    type="button"
                    onClick={() => !addonRequired && setAddonEnabled(v => !v)}
                    disabled={addonRequired}
                    className="w-full flex items-center justify-between gap-3"
                    data-testid="ocr-addon-toggle"
                  >
                    <div className="text-left min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{addonLabel}</div>
                      <div className="text-[11px] text-slate-400">
                        {addonRequired ? 'Obligatorio por configuracion' : 'Suma evento al cobro de estacionamiento'}
                      </div>
                    </div>
                    <span
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
                        (addonEnabled || addonRequired) ? 'bg-amber-500' : 'bg-slate-600'
                      } ${addonRequired ? 'opacity-80 cursor-not-allowed' : ''}`}
                      role="switch"
                      aria-checked={addonEnabled || addonRequired}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                          (addonEnabled || addonRequired) ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </span>
                  </button>

                  {(addonEnabled || addonRequired) && (
                    <Select value={addonEventId} onValueChange={setAddonEventId}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white event-select-trigger" data-testid="ocr-addon-select">
                        <SelectValue placeholder="Seleccionar evento..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-72 event-select-content">
                        {eventTypes.map(et => (
                          <SelectItem
                            key={et.id}
                            value={et.id}
                            className="text-slate-100 focus:bg-slate-700 focus:text-white data-[state=checked]:bg-emerald-700/40 data-[state=checked]:text-white"
                          >
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full" style={{ background: et.color || '#10b981' }} />
                              <span>{et.name}</span>
                              <span className="text-emerald-400 ml-2 font-mono">{formatCurrency(et.price)}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {autoMode && (addonEnabled || addonRequired) && addonEventId && (
                    <p className="text-[11px] text-amber-200/80">
                      Modo Auto: cada entrada detectada se cobrara con este evento.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Total estimate — parking + evento (manual y auto) */}
            {tariff && (
              <div className="entry-total-card rounded-xl bg-slate-900/60 border border-slate-700 p-3" data-testid="ocr-entry-total">
                {(() => {
                  const ev = eventTypes.find((e) => e.id === addonEventId);
                  const parkingNow = getParkingPrice(vehicleType, tariff);
                  const eventNow = (addonEnabled || addonRequired) && ev ? (ev.price || 0) : 0;
                  const totalNow = (parkingNow || 0) + eventNow;
                  return (
                    <>
                      <div className="flex items-center justify-between text-slate-200 text-sm">
                        <span className="flex items-center gap-2"><Car className="w-4 h-4 text-blue-400" />Estacionamiento</span>
                        <span className="font-mono">
                          {formatCurrency(parkingNow)}
                        </span>
                      </div>
                      {eventNow > 0 && (
                        <div className="flex items-center justify-between text-slate-200 text-sm mt-2">
                          <span className="flex items-center gap-2 truncate">
                            <CalendarDays className="w-4 h-4 text-amber-400 shrink-0" />
                            Evento ({ev?.name})
                          </span>
                          <span className="font-mono text-amber-300">{formatCurrency(eventNow)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                        <span className="text-slate-400 text-xs uppercase tracking-wide">Total</span>
                        <span className="text-emerald-400 font-mono font-bold text-lg">{formatCurrency(totalNow)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Auto mode info */}
            {autoMode && (
              <Card className="bg-emerald-900/30 border-emerald-700/50">
                <CardContent className="p-4">
                  <p className="text-emerald-300 text-sm">
                    <strong>Modo Automatico:</strong> Lee la patente cada {ocrSettings.interval >= 1000 ? `${ocrSettings.interval/1000}s` : `${ocrSettings.interval}ms`}. 
                    Si el vehiculo esta adentro, registra salida. Si no, registra entrada.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* OCR Settings */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowSettings(!showSettings)}>
                <CardTitle className="text-white text-base flex items-center justify-between">
                  <span className="flex items-center gap-2"><Settings className="w-4 h-4 text-blue-400" />Ajustes OCR</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showSettings ? 'rotate-180' : ''}`} />
                </CardTitle>
              </CardHeader>
              {showSettings && (
                <CardContent className="space-y-4 pt-2" data-testid="detection-settings-panel">
                  <div>
                    <div className="flex justify-between text-xs text-slate-400 mb-1.5"><span>Sensibilidad</span><span className="text-white font-mono">{ocrSettings.sensitivity}%</span></div>
                    <Slider value={[ocrSettings.sensitivity]} min={5} max={80} step={5} onValueChange={([v]) => setOcrSettings(s => ({...s, sensitivity: v}))} />
                    <p className="text-[10px] text-slate-500 mt-1">{ocrSettings.sensitivity <= 20 ? 'Muy sensible (mas lecturas, mas errores)' : ocrSettings.sensitivity >= 50 ? 'Estricto (menos errores)' : 'Equilibrado'}</p>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-slate-400 mb-1.5"><span>Calidad imagen</span><span className="text-white font-mono">{ocrSettings.quality}%</span></div>
                    <Slider value={[ocrSettings.quality]} min={20} max={90} step={10} onValueChange={([v]) => setOcrSettings(s => ({...s, quality: v}))} />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-slate-400 mb-1.5"><span>Resolucion</span><span className="text-white font-mono">{ocrSettings.resolution}px</span></div>
                    <Slider value={[ocrSettings.resolution]} min={320} max={640} step={40} onValueChange={([v]) => setOcrSettings(s => ({...s, resolution: v}))} />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-slate-400 mb-1.5"><span>Intervalo</span><span className="text-white font-mono">{ocrSettings.interval >= 1000 ? `${ocrSettings.interval/1000}s` : `${ocrSettings.interval}ms`}</span></div>
                    <Slider value={[ocrSettings.interval]} min={500} max={3000} step={250} onValueChange={([v]) => setOcrSettings(s => ({...s, interval: v}))} />
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Manual mode info */}
            {!autoMode && (
              <Card className="bg-blue-900/30 border-blue-700/50">
                <CardContent className="p-4">
                  <p className="text-blue-300 text-sm">
                    <strong>Modo Manual:</strong> Usa "Leer Patente" para detectar el texto. 
                    Luego confirmá la entrada desde el formulario.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
